import type { ListBuilderConfiguration } from '../types';

/**
 * Shared icon value mapping for conversation modes
 */
const CONVERSATION_ICON_VALUE_MAPPING = [
	{ value: 'phone', iconName: 'phone' },
	{ value: 'waveform', iconName: 'waveform' },
	{ value: 'comment', iconName: 'comment' },
];

/**
 * Conversation Session list configuration
 * Shows AI agent conversation sessions
 * Converted from JSON to TypeScript for type safety
 */
export const CONVERSATION_SESSION_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-sessions-all',
			cacheTTL: 600000,
			name: 'All Sessions',
			type: 'default',
			active: true,
			defaultSortId: 'created-date-desc',
			columns: [
				{
					id: 'session-info',
					fields: [
						{
							id: 'session-id',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_id',
									objectTable: '_conversation_session',
								},
							},
							styleConfig: {
								size: 'xs',
								weight: 'medium',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								prefix: '#',
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-id',
							displayStyle: 'line_break',
						},
						{
							id: 'start-time',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
									objectTable: '_conversation_session',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'datetime-medium' },
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-date',
							displayStyle: 'line_break',
						},
						{
							id: 'agent-icon',
							dataConfig: { type: 'custom_text', customText: 'robot' },
							styleConfig: {
								size: 'xs',
								iconName: 'robot',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'icon',
						},
						{
							id: 'agent-name',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'agentName',
									objectTable: '_conversation_session',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Session' }],
					sortable: true,
					alignment: 'left',
					verticalAlignment: 'middle',
					widthConfig: { mode: 'fixed', width: 200 },
				},
				{
					id: 'conversation',
					fields: [
						{
							id: 'mode-icon',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'conversation_icon',
									objectTable: '_conversation_session',
								},
							},
							styleConfig: {
								size: 'sm',
								iconName: 'phone',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								valueMapping: CONVERSATION_ICON_VALUE_MAPPING,
							},
							displayStyle: 'icon',
						},
						{
							id: 'contact-display',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'contact_display',
									objectTable: '_conversation_session',
								},
								defaultValue: 'Unknown',
							},
							styleConfig: {
								size: 'md',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							displayStyle: 'line_break',
						},
						{
							id: 'clock-icon',
							dataConfig: { type: 'custom_text', customText: 'clock' },
							styleConfig: {
								size: 'xs',
								iconName: 'clock',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'icon',
						},
						{
							id: 'duration',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'duration',
									objectTable: '_conversation_session',
								},
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { customFormat: 'duration-seconds' },
							},
							displayStyle: 'text',
						},
						{
							id: 'message-icon',
							dataConfig: { type: 'custom_text', customText: 'message' },
							styleConfig: {
								size: 'xs',
								iconName: 'message',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'icon',
						},
						{
							id: 'message-count',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'message_count',
									objectTable: '_conversation_session',
								},
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { numberFormat: 'comma', decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'token-icon',
							dataConfig: { type: 'custom_text', customText: 'coins' },
							styleConfig: {
								size: 'xs',
								iconName: 'coins',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'icon',
						},
						{
							id: 'token-count',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'token_count',
									objectTable: '_conversation_session',
								},
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: {
									numberFormat: 'abbreviated',
									decimalPlaces: 1,
								},
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h1', text: 'Conversation' }],
					sortable: true,
					alignment: 'left',
					verticalAlignment: 'middle',
					widthConfig: { mode: 'flexible', preset: 'large' },
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'details-button',
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'viewSession',
							},
							dataConfig: { type: 'custom_text', customText: 'View Details' },
							styleConfig: {
								size: 'sm',
								iconName: 'arrow-right',
								variant: 'outline',
								colorRule: { type: 'default' },
							},
							displayStyle: 'button',
						},
					],
					headers: [{ id: 'h3', text: 'Actions' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
					widthConfig: { mode: 'fixed', width: 160 },
				},
			],
			description: 'All AI agent conversation sessions',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'sessionSearch',
			'sessionAgent',
			'sessionDateRange',
			'sessionMode',
			'sessionStatus',
			'sessionSuccess',
			'callProvider',
			'targetPhone',
			'sessionBatch',
			'sessionOutcome',
			'sessionRating',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/ai-agents/conversations/:id',
			objectLabel: 'Session',
			objectTable: '_conversation_session',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: '_created_date', fieldLabel: 'Start Time' },
				{ fieldName: '_modified_date', fieldLabel: 'Modified Time' },
				{ fieldName: 'token_count', fieldLabel: 'Tokens Used' },
				{ fieldName: 'message_count', fieldLabel: 'Messages' },
				{ fieldName: 'duration', fieldLabel: 'Duration' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'success', fieldLabel: 'Success' },
				{ fieldName: 'conversation_mode', fieldLabel: 'Mode' },
				{ fieldName: 'call_status', fieldLabel: 'Call Status' },
				{ fieldName: 'agentName', fieldLabel: 'Agent Name' },
				{ fieldName: 'contact_display', fieldLabel: 'Contact' },
				{ fieldName: 'contact_type', fieldLabel: 'Contact Type' },
				{ fieldName: 'creator_name', fieldLabel: 'Creator' },
			],
		},
	],
	tableSorts: [
		{
			id: 'created-date-desc',
			name: 'Newest First',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'createdDate',
					objectTable: '_conversation_session',
				},
			],
			description: 'Sort by session creation date, newest first',
		},
		{
			id: 'created-date-asc',
			name: 'Oldest First',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'createdDate',
					objectTable: '_conversation_session',
				},
			],
			description: 'Sort by session creation date, oldest first',
		},
	],
};
